import type { Catalog } from './catalog';
import { interpolate } from './interpolate';
import { pluralCategoryKey } from './plural';

export type Translator = {
  (key: string, vars?: Readonly<Record<string, string | number>>): string;
  has(key: string): boolean;
  /**
   * The count-bearing form of `t`: resolves `${key}_one` or `${key}_other` by the
   * locale's `Intl.PluralRules` (`plural.ts`), interpolating `{n}` = `count` over
   * `vars`. A miss is the same loud `⟦variant⟧` as `t`. Bound, so a wrapper may
   * re-export it detached (`export const tPlural = t.plural`).
   */
  plural(key: string, count: number, vars?: Readonly<Record<string, string | number>>): string;
  /**
   * The locale this translator was built for.
   *
   * A translator IS a catalog bound to a locale, and every consumer already
   * holds one wherever copy is rendered — so the locale rides along instead of
   * being threaded a second time beside it. That second thread is what was
   * missing when `/es/pricing` printed `$1,100` from a formatter and `$1.100`
   * from the catalog on the same page (#3798): the money formatter had no
   * locale, and the only alternative was passing one through every prop builder
   * that already takes a `t`.
   */
  readonly locale: string;
};

export function createTranslator(catalog: Catalog, locale: string): Translator {
  // `Object.hasOwn`, never a bare `catalog[key]`: a plain object inherits from
  // Object.prototype, so `t('toString')` resolved to a FUNCTION (rendered as native
  // source) and `t('constructor', vars)` threw `template.replace is not a function`
  // from inside a view. It also disagreed with `has()`, which has always used
  // hasOwn. One lookup rule, so the two can never answer differently.
  const own = (key: string): string | undefined =>
    Object.hasOwn(catalog, key) ? catalog[key] : undefined;
  const t = (key: string, vars?: Readonly<Record<string, string | number>>): string => {
    const template = own(key);
    if (template === undefined) {
      // Loud miss so views never silently render the wrong text.
      return `⟦${key}⟧`;
    }
    return vars ? interpolate(template, vars) : template;
  };
  t.has = (key: string): boolean => Object.hasOwn(catalog, key);
  const rules = new Intl.PluralRules(locale);
  t.plural = (
    key: string,
    count: number,
    vars?: Readonly<Record<string, string | number>>,
  ): string => t(pluralCategoryKey(rules, key, count), { ...vars, n: count });
  t.locale = locale;
  return t;
}
